<template>
  <DatatableReportV3
    id="custom-forms-table"
    ref="table"
    :source="source"
    :config="config"
    :filters="filters"
    :map-row-class="mapRowClass"
    title="Forms &amp; Checklists"
    download-url="/company/custom_forms/create_report"
  >
    <template #actions>
      <SeraButton
        icon="fa fa-plus fa-fw"
        text="New form"
        data-cy="new-custom-form"
        @click="goToBuilder"
      />
    </template>
  </DatatableReportV3>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";

import SeraButton from "@/components/Button.vue";
import { customFormNewPath } from "@/mixins/path_helpers";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import CustomFormActionsMenu from "./CustomFormActionsMenu.vue";
import CustomFormStatusChip from "./CustomFormStatusChip.vue";

defineProps<{ source: string; filters: any[] }>();

const router = useRouter();

function goToBuilder() {
  router.push(customFormNewPath());
}

// Muted, struck-through row for archived templates so they read as inactive
// without being hidden. Return an array of single-token classes — the datatable
// feeds these straight to classList.add(), which rejects space-separated strings.
// The datatable serializes booleans as strings, so "false" is truthy — check the
// archived case explicitly rather than relying on truthiness.
function mapRowClass({
  data
}: {
  data: { archived?: boolean | string };
}): string[] {
  const isArchived = data.archived === true || data.archived === "true";
  return isArchived ? ["line-through", "text-gray-400"] : [];
}

const config = {
  responsive: true,
  order: [[0, "asc"]],
  columns: [
    {
      data: "name",
      title: "Name",
      className: "wrap-when-responsive",
      responsivePriority: 1
    },
    {
      data: "departments",
      title: "Departments",
      filterName: "department_id",
      orderable: false,
      searchable: false,
      responsivePriority: 3
    },
    {
      data: "service_categories",
      title: "Service Categories",
      filterName: "service_category",
      orderable: false,
      searchable: false,
      responsivePriority: 4
    },
    {
      data: "required_for_close",
      title: "Required to Close",
      searchable: false,
      responsivePriority: 5
    },
    {
      data: "status",
      title: "Status",
      filterName: "custom_form_status",
      orderable: false,
      searchable: false,
      responsivePriority: 2
    },
    {
      data: "updated_at",
      title: "Updated",
      searchable: false,
      responsivePriority: 6
    },
    { data: "actions", title: "", orderable: false, searchable: false }
  ],
  vueComponents: [
    { rootId: "custom-form-status-chip-vue", component: CustomFormStatusChip },
    { rootId: "custom-form-actions-menu-vue", component: CustomFormActionsMenu }
  ]
};
</script>
